import { useMemo, useState } from 'react'

import cn from 'classnames'

import ChainToken from '@src/components/ChainToken'
import ChevronDownIcon from '@src/components/icons/ChevronDown'
import CloseIcon from '@src/components/icons/Close'
import ProcessFailedIcon from '@src/components/icons/ProcessFailed'
import { StepNames } from '@src/enums'
import { LiquiditySource } from '@src/enums'
import SwapModalFooter from '@src/pages/Exchange/components/SwapInProgressFlow/SwapModalFooter'
import TransactionDetails from '@src/pages/Exchange/components/SwapInProgressFlow/TransactionDetails'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { useConfigStore } from '@src/zustand_stores'

interface ISwapFailedProps {
    sellData: IExchangeData
    buyData: IExchangeData
    result?: {
        sourceTxHash?: string
        message?: string
    }
    currentStep: StepNames
    fees: IFee[]
    destinationAddress?: string
    liquiditySource: LiquiditySource
}

export default function SwapFailed({
    sellData,
    buyData,
    result = undefined,
    currentStep,
    fees,
    destinationAddress,
    liquiditySource,
}: ISwapFailedProps) {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const [showDetails, setShowDetails] = useState(true)
    const transactionHash = useMemo(() => {
        if (result?.sourceTxHash) {
            const urlParts = result.sourceTxHash.split('/')
            return urlParts[urlParts.length - 1]
        }
        return ''
    }, [result?.sourceTxHash])

    return (
        <div className='space-y-10 px-4'>
            <div className='flex w-full justify-center'>
                <p className='text-base font-medium text-[#E84142]'>Swap Failed</p>
            </div>
            <div className='relative flex h-[212px] items-center justify-between rounded-3xl border border-[#1A1B20] px-8'>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={sellData.token?.logoURI}
                        networkIcon={availableNetworks[sellData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
                <div className='absolute left-1/2 top-0 flex h-full -translate-x-1/2 items-center justify-between gap-2'>
                    <ProcessFailedIcon />
                    <div className='flex h-5 w-5 items-center justify-center rounded-full bg-[#16171A]'>
                        <CloseIcon className='text-[#E84142]' />
                    </div>
                    <ProcessFailedIcon />
                </div>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={buyData.token?.logoURI}
                        networkIcon={availableNetworks[buyData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
            </div>
            <div className='space-y-6'>
                {result?.message && (
                    <div className='flex justify-center'>
                        <span className='text-sm text-mach-sub-text'>{result.message}</span>
                    </div>
                )}
                <div className='flex items-center gap-4'>
                    <div className='flex-grow border border-[#313339]'></div>
                    <div
                        className='flex cursor-pointer items-center gap-1'
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        <span className='text-sm text-mach-accent-forgeround'>Details</span>
                        <ChevronDownIcon className={cn('h-4 w-4 transition-all', !showDetails ? 'rotate-180' : '')} />
                    </div>
                    <div className='flex-grow border border-[#313339]'></div>
                </div>
                {
                    <div
                        className={cn(
                            'flex flex-col overflow-hidden transition-all duration-500',
                            showDetails ? (transactionHash ? 'h-[284px]' : 'h-[242px]') : 'h-0'
                        )}
                    >
                        <div className='space-y-6'>
                            <TransactionDetails
                                sellData={sellData}
                                buyData={buyData}
                                fees={fees}
                                destinationAddress={destinationAddress}
                                transactionHash={transactionHash}
                                currentStep={currentStep}
                                liquiditySource={liquiditySource}
                                // do not show this here (dup info w/ the rest of the flow)
                                isCCTPTransfer={false}
                            />
                        </div>
                    </div>
                }
                {result?.sourceTxHash && <SwapModalFooter transactionHash={result.sourceTxHash} />}
            </div>
        </div>
    )
}
